import React, { useMemo, useRef } from "react";
import { View, StyleSheet, Pressable } from "react-native";
import { useTheme } from "@/hooks/useTheme";
import { useToolTip } from "@/hooks/useToolTip";
import { ThemedText } from "@/components/ThemedText";
import { TIME_MODIFIERS, TimeModifierCode } from "@/types/db/time_cards";
import type { TimeData, TimeSegment } from "./types";

export type TimeBarProps = {
  value: TimeData | null | undefined;
  // Reserved for future use
  showModifiers?: boolean;
  showText?: boolean;
};

const BAR_HEIGHT = 14;
const BORDER_RADIUS = 8;

export function TimeBar({ value }: TimeBarProps) {
  const { colors } = useTheme();

  // Parse value if it's a JSON string
  const parsedValue = useMemo(() => {
    if (!value) return null;
    if (typeof value === "string") {
      try {
        return JSON.parse(value) as TimeData;
      } catch {
        return null;
      }
    }
    return value;
  }, [value]);

  // Segment type colors
  const workedColor = colors.complete?.border || "#10b981";
  const mealColor = colors.pending?.border || "#f59e0b";
  const breakColor = colors.neutral?.border || "#6b7280";
  const nonWorkedColor = colors.primary || "#3b82f6";

  const getSegmentColor = (type: TimeSegment["type"]) => {
    switch (type) {
      case "worked":
        return workedColor;
      case "meal":
        return mealColor;
      case "break":
        return breakColor;
      case "non-worked":
        return nonWorkedColor;
    }
  };

  const segmentBars = useMemo(() => {
    if (!parsedValue?.segments || parsedValue.segments.length === 0) {
      return [];
    }

    const segments = parsedValue.segments;
    const totalDuration = segments.reduce((sum, seg) => sum + seg.duration, 0);

    if (totalDuration === 0) {
      return [];
    }

    return segments.map((segment, index) => {
      const widthPercent = (segment.duration / totalDuration) * 100;
      const isFirst = index === 0;
      const isLast = index === segments.length - 1;

      return {
        id: segment.id,
        type: segment.type,
        widthPercent,
        isFirst,
        isLast,
        // Pass through for future use
        billable: segment.billable,
        payable: segment.payable,
        modifiers: segment.modifiers,
        duration: segment.duration,
      };
    });
  }, [parsedValue]);

  if (!parsedValue?.segments || parsedValue.segments.length === 0) {
    return null;
  }

  return (
    <View style={styles.container}>
      {segmentBars.map((bar) => (
        <SegmentBar key={bar.id} bar={bar} color={getSegmentColor(bar.type)} colors={colors} />
      ))}
    </View>
  );
}

// Individual segment with tooltip
type SegmentBarProps = {
  bar: {
    id: string;
    type: TimeSegment["type"];
    widthPercent: number;
    isFirst: boolean;
    isLast: boolean;
    duration: number;
    billable?: boolean;
    payable?: boolean;
    modifiers?: TimeModifierCode[];
  };
  color: string;
  colors: ReturnType<typeof useTheme>["colors"];
};

// Format duration as hours (e.g., "2.5h" or "45m")
function formatDuration(hours: number): string {
  if (hours < 1) {
    return `${Math.round(hours * 60)}m`;
  }
  return hours % 1 === 0 ? `${hours}h` : `${hours.toFixed(1)}h`;
}

// Format segment type for display
function formatType(type: TimeSegment["type"]): string {
  switch (type) {
    case "worked":
      return "Worked";
    case "meal":
      return "Meal";
    case "break":
      return "Break";
    case "non-worked":
      return "Non-worked";
  }
}

function SegmentBar({ bar, color, colors }: SegmentBarProps) {
  const toolTip = useToolTip();
  const ref = useRef<View>(null);

  const handleHoverIn = () => {
    toolTip.setToolTipContent(
      <View style={[styles.tooltip, { backgroundColor: colors.background, borderColor: colors.border }]}>
        {/* Header: Type + Duration */}
        <View style={styles.tooltipHeader}>
          <View style={[styles.typeIndicator, { backgroundColor: color }]} />
          <ThemedText style={styles.typeText}>{formatType(bar.type)}</ThemedText>
          <ThemedText style={[styles.durationText, { color: colors.placeholder }]}>
            {formatDuration(bar.duration)}
          </ThemedText>
        </View>

        {/* Status row: Billable / Payable */}
        <View style={styles.statusRow}>
          <View style={styles.statusItem}>
            <ThemedText
              style={[styles.statusIcon, { color: bar.billable ? colors.complete.text : colors.placeholder }]}
            >
              {bar.billable ? "\u2713" : "\u2717"}
            </ThemedText>
            <ThemedText style={[styles.statusLabel, { color: colors.placeholder }]}>Billable</ThemedText>
          </View>
          <View style={styles.statusItem}>
            <ThemedText style={[styles.statusIcon, { color: bar.payable ? colors.complete.text : colors.placeholder }]}>
              {bar.payable ? "\u2713" : "\u2717"}
            </ThemedText>
            <ThemedText style={[styles.statusLabel, { color: colors.placeholder }]}>Payable</ThemedText>
          </View>
        </View>

        {/* Modifiers */}
        {bar.modifiers && bar.modifiers.length > 0 && (
          <View style={styles.modifiersRow}>
            {bar.modifiers.map((mod) => (
              <View key={mod} style={[styles.modifierChip, { backgroundColor: TIME_MODIFIERS[mod].color + "20" }]}>
                <ThemedText style={[styles.modifierText, { color: TIME_MODIFIERS[mod].color }]}>{mod}</ThemedText>
              </View>
            ))}
          </View>
        )}
      </View>,
    );
    ref.current?.measureInWindow((pageX, pageY, width) => {
      // Center above the segment
      // Base offset: header (~20) + statusRow (~18) + padding (16) + gap (12) + border (2) + buffer (12) = ~80
      let yOffset = 80;
      // Add height for modifiers row if present: chip height (~18) + marginTop (2) + gap (6) + buffer
      if (bar.modifiers && bar.modifiers.length > 0) {
        yOffset += 36;
      }
      toolTip.setLocation({ x: pageX + width / 2 - 70, y: pageY - yOffset } as any);
      toolTip.setVisible(true);
    });
  };

  const handleHoverOut = () => {
    toolTip.setVisible(false);
  };

  // Get type initial for display
  const typeInitial = bar.type === "non-worked" ? "N" : bar.type.charAt(0).toUpperCase();

  // Show more content based on width percentage
  const showDuration = bar.widthPercent >= 15;
  const showTypeInitial = bar.widthPercent >= 8 && bar.widthPercent < 15;

  return (
    <Pressable
      ref={ref}
      onHoverIn={handleHoverIn}
      onHoverOut={handleHoverOut}
      style={[
        styles.segment,
        {
          width: `${bar.widthPercent}%`,
          backgroundColor: color,
          borderTopLeftRadius: bar.isFirst ? BORDER_RADIUS : 0,
          borderBottomLeftRadius: bar.isFirst ? BORDER_RADIUS : 0,
          borderTopRightRadius: bar.isLast ? BORDER_RADIUS : 0,
          borderBottomRightRadius: bar.isLast ? BORDER_RADIUS : 0,
        },
      ]}
    >
      {showDuration && (
        <ThemedText style={styles.segmentText} numberOfLines={1}>
          {formatDuration(bar.duration)}
        </ThemedText>
      )}
      {showTypeInitial && (
        <ThemedText style={styles.segmentText} numberOfLines={1}>
          {typeInitial}
        </ThemedText>
      )}
    </Pressable>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
    height: BAR_HEIGHT,
    flex: 1,
    overflow: "hidden",
  },
  segment: {
    height: "100%",
    justifyContent: "center",
    alignItems: "center",
    overflow: "hidden",
  },
  segmentText: {
    fontSize: 9,
    fontWeight: "600",
    color: "white",
    // textShadow is a web-only style not modeled by RN's TextStyle
    textShadow: "0px 1px 1px rgba(0,0,0,0.3)",
  } as any,
  tooltip: {
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 10,
    paddingVertical: 8,
    gap: 6,
    minWidth: 140,
  },
  tooltipHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  typeIndicator: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  typeText: {
    fontSize: 13,
    fontWeight: "600",
  },
  durationText: {
    fontSize: 12,
    marginLeft: "auto",
  },
  statusRow: {
    flexDirection: "row",
    gap: 12,
  },
  statusItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  statusIcon: {
    fontSize: 11,
    fontWeight: "600",
  },
  statusLabel: {
    fontSize: 11,
  },
  modifiersRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 4,
    marginTop: 2,
  },
  modifierChip: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
  },
  modifierText: {
    fontSize: 10,
    fontWeight: "600",
  },
});
